/* eslint-disable @typescript-eslint/no-unsafe-assignment */
import { useState } from 'react';
import { Lock, Eye, EyeOff, CheckCircle2, XCircle, Clock, Fingerprint, Globe } from 'lucide-react';

import type { ProcessedEvent } from './inspector-context';
import { JsonHighlight } from './json-highlight';

interface StatePanelProps {
  events: ProcessedEvent[];
  startAt: number;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatBytes(bytes: number): string {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
}

function deepWalk(value: unknown): unknown {
  if (typeof value === 'string') {
    const trimmed = value.trim();
    const looksLike = trimmed.startsWith('{') || trimmed.startsWith('[') || trimmed.startsWith('"');
    if (!looksLike) return value;
    try {
      const parsed = JSON.parse(trimmed);
      if (typeof parsed === 'string') return deepWalk(parsed);
      return deepWalk(parsed);
    } catch {
      return value;
    }
  }
  if (Array.isArray(value)) return value.map(deepWalk);
  if (value !== null && typeof value === 'object') {
    return Object.fromEntries(
      Object.entries(value as Record<string, unknown>).map(([k, v]): [string, unknown] => [
        k,
        deepWalk(v),
      ]),
    );
  }
  return value;
}

// ─── Section wrapper ──────────────────────────────────────────────────────────

function Section({
  title,
  badge,
  children,
}: {
  title: string;
  badge?: string | number;
  children: React.ReactNode;
}): React.ReactNode | null {
  return (
    <div>
      <div className="flex items-center gap-2 mb-2">
        <h4 className="text-[9px] font-bold text-muted-foreground/40 uppercase tracking-widest">
          {title}
        </h4>
        {badge !== undefined && (
          <span className="px-1.5 py-0.5 rounded text-[9px] font-mono bg-white/[0.05] text-muted-foreground/40">
            {badge}
          </span>
        )}
      </div>
      {children}
    </div>
  );
}

// ─── Execution Summary card ───────────────────────────────────────────────────

function ExecutionSummaryV2({
  events,
  startAt,
}: {
  events: ProcessedEvent[];
  startAt: number;
}): React.ReactNode | null {
  const bootEvent = events.find((e): boolean => e.capability === 'vm' && e.action === 'boot');
  const returnEvent = events.find((e): boolean => e.capability === 'vm' && e.action === 'return');
  const errorEvent = events.find(
    (e): boolean => e.phase === 'error' || (e.phase === 'span' && e.errorPayload !== undefined),
  );

  const bootPayload = (
    bootEvent?.phase === 'span' ? bootEvent.requestPayload : bootEvent?.payload
  ) as Record<string, unknown> | null;
  const returnPayload =
    returnEvent?.phase === 'span' ? returnEvent.requestPayload : returnEvent?.payload;
  const errorPayload = (
    errorEvent?.phase === 'span' ? errorEvent.errorPayload : errorEvent?.payload
  ) as Record<string, unknown> | null;

  const args = bootPayload?.['args'] ?? bootPayload?.['0'] ?? bootPayload;
  const entry = bootPayload?.['entry'] ?? bootPayload?.['name'] ?? null;
  const requestId = bootPayload?.['requestId'] ?? null;
  const endAt = returnEvent?.at ?? events[events.length - 1]?.at ?? 0;
  const durationMs = Math.round(endAt - startAt);
  const status = errorEvent ? 'error' : 'success';

  const errMsg =
    errorPayload?.['message'] ?? errorPayload?.['error'] ?? errorPayload?.['msg'] ?? null;

  return (
    <div
      className={`rounded-xl border p-4 space-y-3 ${status === 'error' ? 'border-[var(--flux-error)]/20 bg-[var(--flux-error)]/[0.03]' : 'border-[var(--flux-success)]/15 bg-[var(--flux-success)]/[0.02]'}`}
    >
      {/* Status row */}
      <div className="flex items-center gap-3">
        {status === 'error' ? (
          <XCircle className="h-4 w-4 text-[var(--flux-error)] shrink-0" />
        ) : (
          <CheckCircle2 className="h-4 w-4 text-[var(--flux-success)] shrink-0" />
        )}
        <span
          className={`text-sm font-semibold font-mono ${status === 'error' ? 'text-[var(--flux-error)]' : 'text-[var(--flux-success)]'}`}
        >
          {status === 'error' ? 'Failed' : 'Succeeded'}
        </span>
        <span className="flex items-center gap-1 ml-auto text-[10px] font-mono text-muted-foreground/40">
          <Clock className="h-3 w-3" />
          {durationMs}ms
        </span>
      </div>

      {/* Entry / req ID */}
      {Boolean(entry ?? requestId) && (
        <div className="flex flex-wrap gap-x-6 gap-y-1 text-[10px] font-mono">
          {Boolean(entry) && (
            <span className="text-muted-foreground/50">
              fn: <span className="text-foreground/70">{entry as string}</span>
            </span>
          )}
          {Boolean(requestId) && (
            <span className="text-muted-foreground/50">
              req: <span className="text-foreground/70">{(requestId as string).slice(0, 20)}</span>
            </span>
          )}
        </div>
      )}

      {/* Error message */}
      {Boolean(errMsg) && (
        <div className="px-3 py-2 rounded-lg bg-[var(--flux-error)]/[0.08] border border-[var(--flux-error)]/20 text-[11px] font-mono text-[var(--flux-error)]">
          {errMsg as string}
        </div>
      )}

      {/* Args — prettified */}
      {Boolean(args !== null && args !== undefined) && (
        <div>
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-[9px] font-mono text-muted-foreground/30 uppercase tracking-widest block">
              Input arguments
            </span>
            {((): import('react/jsx-runtime').JSX.Element | null => {
              try {
                const str = JSON.stringify(args);
                const size = new Blob([str]).size;
                return (
                  <span className="text-[9px] font-mono text-muted-foreground/40 bg-white/5 px-1.5 py-0.5 rounded">
                    {formatBytes(size)}
                  </span>
                );
              } catch {
                return null;
              }
            })()}
          </div>
          <div className="bg-[#0d1117] rounded-lg px-3 py-2 overflow-auto max-h-40">
            <JsonHighlight value={deepWalk(args)} />
          </div>
        </div>
      )}

      {/* Return value */}
      {Boolean(returnPayload !== null && returnPayload !== undefined && !errorEvent) && (
        <div>
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-[9px] font-mono text-muted-foreground/30 uppercase tracking-widest block">
              Return value
            </span>
            {((): import('react/jsx-runtime').JSX.Element | null => {
              try {
                const str = JSON.stringify(returnPayload);
                const size = new Blob([str]).size;
                return (
                  <span className="text-[9px] font-mono text-muted-foreground/40 bg-white/5 px-1.5 py-0.5 rounded">
                    {formatBytes(size)}
                  </span>
                );
              } catch {
                return null;
              }
            })()}
          </div>
          <div className="bg-[#0d1117] rounded-lg px-3 py-2 overflow-auto max-h-40">
            <JsonHighlight value={deepWalk(returnPayload)} />
          </div>
        </div>
      )}
    </div>
  );
}

// ─── HTTP / API Context card ──────────────────────────────────────────────────

function HttpContext({
  payload,
}: {
  payload: Record<string, unknown> | null | undefined;
}): import('react/jsx-runtime').JSX.Element | null {
  if (!payload) return null;
  const method = typeof payload['method'] === 'string' ? payload['method'] : null;
  const url = typeof payload['url'] === 'string' ? payload['url'] : null;
  const headers = payload['headers'] as Record<string, string> | undefined;

  if (!method && !url && (!headers || Object.keys(headers).length === 0)) return null;

  return (
    <div className="rounded-xl border border-white/5 bg-white/[0.01] p-4 space-y-3">
      <div className="flex items-center gap-2">
        <Globe className="h-4 w-4 text-[var(--flux-fetch)]" />
        <span className="text-sm font-semibold text-foreground/80 font-mono">HTTP Context</span>
      </div>

      {Boolean(url) && (
        <div className="flex flex-col gap-1">
          <span className="text-[9px] font-mono text-muted-foreground/30 uppercase tracking-widest">
            Request
          </span>
          <div className="flex gap-2">
            <span className="text-[11px] font-bold text-foreground font-mono bg-white/5 px-1.5 py-0.5 rounded uppercase">
              {method ?? 'GET'}
            </span>
            <span className="text-[11px] font-mono text-muted-foreground truncate">{url}</span>
          </div>
        </div>
      )}

      {Boolean(headers && Object.keys(headers).length > 0) && (
        <div>
          <span className="text-[9px] font-mono text-muted-foreground/30 uppercase tracking-widest block mb-1.5">
            Headers
          </span>
          <div className="bg-[#0d1117] rounded-lg px-3 py-2 overflow-auto max-h-40">
            <table className="w-full text-xs font-mono">
              <tbody>
                {Object.entries(headers ?? {})
                  .filter(([k, v]): boolean => typeof k === 'string' && typeof v === 'string')
                  .map(([key, val]): import('react/jsx-runtime').JSX.Element => (
                    <tr key={key} className="border-b border-white/[0.04] last:border-0">
                      <td className="py-1 pr-3 text-muted-foreground/50 align-top break-keep w-[100px]">
                        {key}
                      </td>
                      <td className="py-1 text-foreground/80 break-all">{val}</td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Identity / Auth Context card ─────────────────────────────────────────────

function AuthContext({
  payload,
}: {
  payload: Record<string, unknown> | null | undefined;
}): import('react/jsx-runtime').JSX.Element | null {
  if (payload?.['auth'] == null) return null;
  const auth = payload['auth'] as Record<string, unknown>;

  if (Object.keys(auth).length === 0) return null;

  const role =
    typeof auth['role'] === 'string'
      ? auth['role']
      : typeof auth['user_role'] === 'string'
        ? auth['user_role']
        : 'Unknown';
  const sub =
    typeof auth['sub'] === 'string'
      ? auth['sub']
      : typeof auth['id'] === 'string'
        ? auth['id']
        : null;

  return (
    <div className="rounded-xl border border-white/5 bg-white/[0.01] p-4 space-y-3">
      <div className="flex items-center gap-2">
        <Fingerprint className="h-4 w-4 text-purple-400" />
        <span className="text-sm font-semibold text-foreground/80 font-mono">Auth Identity</span>
        <span className="ml-auto text-[10px] uppercase font-bold tracking-widest text-purple-400/80 bg-purple-400/10 px-2 py-0.5 rounded">
          {role}
        </span>
      </div>

      {Boolean(sub) && (
        <div className="flex flex-col gap-1">
          <span className="text-[9px] font-mono text-muted-foreground/30 uppercase tracking-widest">
            Subject ID
          </span>
          <span className="text-[11px] font-mono text-muted-foreground">{sub}</span>
        </div>
      )}

      <div>
        <span className="text-[9px] font-mono text-muted-foreground/30 uppercase tracking-widest block mb-1.5">
          Claims
        </span>
        <div className="bg-[#0d1117] rounded-lg px-3 py-2 overflow-auto max-h-40">
          <JsonHighlight value={deepWalk(auth)} />
        </div>
      </div>
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

export function StatePanel({
  events,
  startAt,
}: StatePanelProps): import('react/jsx-runtime').JSX.Element {
  const [showEnvValues, setShowEnvValues] = useState(false);
  const bootEvent = events.find((e): boolean => e.capability === 'vm' && e.action === 'boot');
  const bootPayload = (
    bootEvent?.phase === 'span' ? bootEvent.requestPayload : bootEvent?.payload
  ) as Record<string, unknown> | null;

  // ── Environment variables ──────────────────────────────────────────────────
  const envPairs: { seq: number; key: string; value: string; offset: number }[] = [];
  for (let i = 0; i < events.length; i++) {
    const e = events[i];
    if (e!.phase === 'span' && e!.capability === 'env' && e!.action === 'get') {
      const reqPayload = e!.requestPayload;
      let key = '?';
      if (Array.isArray(reqPayload) && reqPayload.length > 0) key = String(reqPayload[0]);
      else if (
        typeof reqPayload === 'object' &&
        reqPayload !== null &&
        '0' in (reqPayload as Record<string, unknown>)
      )
        key = ((reqPayload as Record<string, unknown>)['0'] as string | undefined) ?? '?';
      else if (typeof reqPayload === 'string') key = reqPayload;
      const resPayload = e!.responsePayload;
      let value = '';
      if (typeof resPayload === 'string') value = resPayload;
      else if (Array.isArray(resPayload) && resPayload.length > 0) value = String(resPayload[0]);
      else if (typeof resPayload === 'object' && resPayload !== null) {
        const obj = resPayload as Record<string, unknown>;
        value =
          (obj['0'] as string | undefined) ??
          (obj['value'] as string | undefined) ??
          JSON.stringify(resPayload);
      }
      envPairs.push({ seq: e!.seq, key, value, offset: Math.round(e!.at - startAt) });
    }
  }

  // ── Checkpoints ─────────────────────────────────────────────────────────────
  const checkpoints: { seq: number; op: string; key: string; value: string; offset: number }[] = [];
  for (const e of events.filter(
    (e): boolean => e.capability === 'checkpoint' && (e.phase === 'span' || e.phase === 'request'),
  )) {
    const p = (e.phase === 'span' ? e.requestPayload : e.payload) as Record<string, unknown> | null;
    const isArgsDump = p != null && '0' in p;
    const op = isArgsDump
      ? ((p['0'] as string | undefined) ?? '?')
      : ((p?.['op'] as string | undefined) ?? (p?.['action'] as string | undefined) ?? '?');
    const key = isArgsDump
      ? ((p['1'] as string | undefined) ?? '?')
      : ((p?.['key'] as string | undefined) ?? '?');
    const rawValue = isArgsDump ? p['2'] : (p?.['value'] ?? p);
    checkpoints.push({
      seq: e.seq,
      op,
      key,
      value: rawValue !== undefined ? JSON.stringify(rawValue) : '-',
      offset: Math.round(e.at - startAt),
    });
  }

  // ── Actual State (Aggregated) ───────────────────────────────────────────────
  const actualState: Record<string, unknown> = {};
  for (const cp of checkpoints) {
    if (cp.op === 'save' || cp.op === 'set' || cp.op === 'put') {
      try {
        const first = JSON.parse(cp.value); // Usually a string of the serialized value
        actualState[cp.key] = typeof first === 'string' ? JSON.parse(first) : first;
      } catch {
        try {
          actualState[cp.key] = JSON.parse(cp.value);
        } catch {
          actualState[cp.key] = cp.value;
        }
      }
    } else if (cp.op === 'delete' || cp.op === 'remove') {
      delete actualState[cp.key];
    }
  }
  const hasActualState = Object.keys(actualState).length > 0;

  // ── Crypto ──────────────────────────────────────────────────────────────────
  const cryptoOps: { action: string; duration: number; offset: number }[] = events
    .filter((e): boolean => e.capability === 'crypto' && e.phase === 'span')
    .map((e) => ({
      action: e.action,
      duration: e.durationMs ?? 0,
      offset: Math.round(e.at - startAt),
    }));

  // ── Timers ──────────────────────────────────────────────────────────────────
  const timers: { type: string; delay: number; offset: number }[] = events
    .filter(
      (e): boolean =>
        (e.capability === 'timer' || e.capability === 'timers') &&
        (e.phase === 'span' || e.phase === 'request'),
    )
    .map((e) => {
      const p = (e.phase === 'span' ? e.requestPayload : e.payload) as Record<
        string,
        unknown
      > | null;
      return {
        type: e.action,
        delay: Number(p?.['delay'] ?? p?.['ms'] ?? 0),
        offset: Math.round(e.at - startAt),
      };
    });

  const noCapabilityData =
    envPairs.length === 0 &&
    checkpoints.length === 0 &&
    cryptoOps.length === 0 &&
    timers.length === 0 &&
    !hasActualState;

  return (
    <div className="p-4 space-y-5">
      {/* 1. Execution summary — always shown */}
      <Section title="Execution">
        <ExecutionSummaryV2 events={events} startAt={startAt} />
      </Section>

      {/* 2. Extracted context block */}
      {Boolean(bootPayload && (bootPayload?.['http'] ?? bootPayload?.['auth'])) && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {Boolean(bootPayload?.['http']) && (
            <HttpContext payload={bootPayload?.['http'] as Record<string, unknown>} />
          )}
          {Boolean(bootPayload?.['auth']) && <AuthContext payload={bootPayload} />}
        </div>
      )}

      {/* 3. Capability sections — only when data exists */}
      {!noCapabilityData && (
        <div className="border-t border-white/5 pt-4 space-y-4">
          {/* Env vars */}
          {envPairs.length > 0 && (
            <Section title="Environment Variables" badge={envPairs.length}>
              <div className="rounded-xl border border-white/8 overflow-hidden">
                <table className="w-full text-xs font-mono">
                  <thead>
                    <tr className="border-b border-white/8 text-muted-foreground/40">
                      <th className="px-3 py-1.5 text-left font-medium">Key</th>
                      <th className="px-3 py-1.5 text-left font-medium">Value</th>
                      <th className="px-3 py-1.5 text-right font-medium">At</th>
                    </tr>
                  </thead>
                  <tbody>
                    {envPairs.map((env, i): import('react/jsx-runtime').JSX.Element => (
                      <tr key={i} className="border-b border-white/[0.04] last:border-0">
                        <td className="px-3 py-1.5 text-[var(--flux-env)]">{env.key}</td>
                        <td className="px-3 py-1.5 text-muted-foreground font-mono">
                          {showEnvValues ? (
                            env.value
                          ) : (
                            <span className="text-muted-foreground/30 tracking-widest">
                              ••••••••
                            </span>
                          )}
                        </td>
                        <td className="px-3 py-1.5 text-right text-muted-foreground/40 tabular-nums">
                          +{env.offset}ms
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <div className="px-3 py-2 flex items-center gap-2 text-[10px] font-mono text-muted-foreground/40 border-t border-white/5">
                  <Lock className="h-3 w-3" />
                  <span>Values masked.</span>
                  <button
                    onClick={(): void => setShowEnvValues((v): boolean => !v)}
                    className="flex items-center gap-1 hover:text-foreground transition-colors ml-0.5"
                  >
                    {showEnvValues ? <EyeOff className="h-3 w-3" /> : <Eye className="h-3 w-3" />}
                    {showEnvValues ? 'Hide' : 'Reveal'}
                  </button>
                </div>
              </div>
            </Section>
          )}

          {/* Actual State */}
          {hasActualState && (
            <Section title="Actual State" badge={Object.keys(actualState).length}>
              <div className="bg-[#0d1117] rounded-xl border border-white/8 px-3 py-2 overflow-auto max-h-60">
                <JsonHighlight value={actualState} />
              </div>
            </Section>
          )}

          {/* Checkpoints */}
          {checkpoints.length > 0 && (
            <Section title="Checkpoints" badge={checkpoints.length}>
              <div className="rounded-xl border border-white/8 overflow-hidden">
                <table className="w-full text-xs font-mono">
                  <thead>
                    <tr className="border-b border-white/8 text-muted-foreground/40">
                      <th className="px-3 py-1.5 text-left font-medium w-12">op</th>
                      <th className="px-3 py-1.5 text-left font-medium">Key</th>
                      <th className="px-3 py-1.5 text-left font-medium">Value</th>
                      <th className="px-3 py-1.5 text-right font-medium">At</th>
                    </tr>
                  </thead>
                  <tbody>
                    {checkpoints.map((cp, i): import('react/jsx-runtime').JSX.Element => (
                      <tr key={i} className="border-b border-white/[0.04] last:border-0">
                        <td className="px-3 py-1.5 text-muted-foreground/50">{cp.op}</td>
                        <td className="px-3 py-1.5 text-[var(--flux-checkpoint)]">{cp.key}</td>
                        <td className="px-3 py-1.5 text-muted-foreground truncate max-w-xs">
                          {cp.value}
                        </td>
                        <td className="px-3 py-1.5 text-right text-muted-foreground/40 tabular-nums">
                          +{cp.offset}ms
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Section>
          )}

          {/* Crypto */}
          {cryptoOps.length > 0 && (
            <Section title="Crypto Operations" badge={cryptoOps.length}>
              <div className="rounded-xl border border-white/8 overflow-hidden">
                <table className="w-full text-xs font-mono">
                  <thead>
                    <tr className="border-b border-white/8 text-muted-foreground/40">
                      <th className="px-3 py-1.5 text-left font-medium">Operation</th>
                      <th className="px-3 py-1.5 text-right font-medium">Duration</th>
                      <th className="px-3 py-1.5 text-right font-medium">At</th>
                    </tr>
                  </thead>
                  <tbody>
                    {cryptoOps.map((op, i): import('react/jsx-runtime').JSX.Element => (
                      <tr key={i} className="border-b border-white/[0.04] last:border-0">
                        <td className="px-3 py-1.5 text-[var(--flux-crypto)]">{op.action}</td>
                        <td className="px-3 py-1.5 text-right text-muted-foreground/60 tabular-nums">
                          {op.duration}ms
                        </td>
                        <td className="px-3 py-1.5 text-right text-muted-foreground/40 tabular-nums">
                          +{op.offset}ms
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Section>
          )}

          {/* Timers */}
          {timers.length > 0 && (
            <Section title="Timers" badge={timers.length}>
              <div className="rounded-xl border border-white/8 overflow-hidden">
                <table className="w-full text-xs font-mono">
                  <thead>
                    <tr className="border-b border-white/8 text-muted-foreground/40">
                      <th className="px-3 py-1.5 text-left font-medium">Type</th>
                      <th className="px-3 py-1.5 text-right font-medium">Delay</th>
                      <th className="px-3 py-1.5 text-right font-medium">At</th>
                    </tr>
                  </thead>
                  <tbody>
                    {timers.map((t, i): import('react/jsx-runtime').JSX.Element => (
                      <tr key={i} className="border-b border-white/[0.04] last:border-0">
                        <td className="px-3 py-1.5 text-[var(--flux-timer)]">{t.type}</td>
                        <td className="px-3 py-1.5 text-right text-muted-foreground/60 tabular-nums">
                          {t.delay}ms
                        </td>
                        <td className="px-3 py-1.5 text-right text-muted-foreground/40 tabular-nums">
                          +{t.offset}ms
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Section>
          )}
        </div>
      )}
    </div>
  );
}
